import { type ChangeEvent, type ReactNode, useRef } from 'react';
import { Avatar, Button, Input, Textarea } from '@/components/ui';
import { SocialLinks } from './SocialLinks';
import { PinnedThemePreviews } from './PinnedThemePreviews';
import { cn } from '@/lib/utils';
import { toast } from '@/components/feedback/toast';
import type { Profile } from '@/types';
import {
  Field,
  SettingsCard,
  TRANSLUCENT_FIELD,
} from '@/features/settings/components/SettingsPrimitives';
import { SaveBar } from '@/features/settings/components/SaveBar';
import { slugifyHandle, STATUS_OPTIONS, type Status } from '@/features/settings/settings';
import type { HandleStatus } from '@/features/settings/hooks/useSettingsData';
import { useProfileEditor } from '../hooks/useProfileEditor';

const STATUS_DOT: Record<Status, string> = {
  online: 'bg-neon-green shadow-[0_0_6px_rgba(57,255,20,0.6)] animate-breathe',
  idle: 'bg-amber-400 shadow-[0_0_6px_rgba(251,191,36,0.6)]',
  offline: 'bg-white/40',
};

const STATUS_TEXT: Record<HandleStatus, { text: string; cls: string } | null> = {
  idle: null,
  checking: { text: 'Checking…', cls: 'text-mid' },
  invalid: { text: 'Invalid format', cls: 'text-danger' },
  taken: { text: 'Taken', cls: 'text-danger' },
  ok: { text: '✓ Available', cls: 'text-neon-green' },
};

/** Read an image file → data URL (OG `readAvatarFile`, 5MB cap). */
function readAvatarFile(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    if (file.size > 5 * 1024 * 1024) return reject(new Error('Photo too large — max 5MB'));
    const fr = new FileReader();
    fr.onload = (e) => resolve(String(e.target?.result ?? ''));
    fr.onerror = () => reject(new Error('Read failed'));
    fr.readAsDataURL(file);
  });
}

/**
 * The "Public profile" editor — avatar, display name, username (with live
 * availability), bio, and status. Ported from the OG settings Profile tab onto
 * the View Profile page; rendered only on the signed-in user's own profile. The
 * floating SaveBar appears whenever the draft is dirty.
 */
export function ProfileEditor({
  profile,
  links = {},
  onAddLink,
  onRemoveLink,
  aside,
  asideFooter,
}: {
  profile: Profile;
  links?: Record<string, string>;
  onAddLink?: (platform: string, url: string) => void;
  onRemoveLink?: (platform: string) => void;
  /** Optional right-hand column rendered beside the form (e.g. the About panel). */
  aside?: ReactNode;
  /** Rendered at the very bottom of the right column, under the links (e.g. the
   *  Locker) — so it shifts down as more links are added. Only used with `aside`. */
  asideFooter?: ReactNode;
}) {
  const { draft, set, dirty, saving, handleStatus, nameLocked, nameUnlockInDays, save, discard } =
    useProfileEditor(profile);
  const fileRef = useRef<HTMLInputElement>(null);

  function onPickAvatar(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    readAvatarFile(file)
      .then((url) => set('avatarUrl', url))
      .catch((err: Error) => toast(err.message, 'error'));
    e.target.value = ''; // allow re-picking the same file
  }

  // Only surface the availability pill when the handle actually differs from the
  // one already on the profile — no point flagging "✓ Available" for a username
  // the user already owns.
  const currentHandle = (profile.handle ?? '').replace(/^@/, '');
  const status = draft.handle === currentHandle ? null : STATUS_TEXT[handleStatus];
  const nameBlank = draft.displayName.trim().length === 0;

  // Links sit with the form when there's no aside, or under About in the right
  // column when there is — so the two columns stay balanced in height.
  const linksField = (
    <Field label="Links">
      <SocialLinks links={links} editable onAdd={onAddLink} onRemove={onRemoveLink} />
    </Field>
  );

  return (
    <>
      <SettingsCard>
        {/* Form on the left; an optional panel (the About controls) fills the
            right-hand deadspace. Single column when there's no aside. The card
            title lives in the left column so it lines up with the About heading. */}
        <div className={cn('grid grid-cols-1 items-start gap-x-6', aside && 'lg:grid-cols-2')}>
          <div className="min-w-0">
            {/* Title — in-column so it aligns with the right column's About heading. */}
            <div className="mb-[18px]">
              <h3 className="text-base font-bold text-hi">Public profile</h3>
              <p className="mt-0.5 text-xs text-mid">How you show up across WINR.</p>
            </div>
            {/* Status — pinned to the top of the card. */}
            <Field label="Status">
              <div className="flex gap-1.5">
                {STATUS_OPTIONS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => set('status', s)}
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-pill border px-3 py-1.5 text-xs font-semibold capitalize transition-all',
                      draft.status === s
                        ? 'border-white/25 bg-white/10 text-hi'
                        : 'border-card-line bg-white/[0.04] text-mid hover:bg-white/[0.08]',
                    )}
                  >
                    <span className={cn('h-2 w-2 rounded-full', STATUS_DOT[s])} />
                    {s}
                  </button>
                ))}
              </div>
            </Field>

            {/* Avatar */}
            <div className="mb-[18px] flex items-center gap-4">
              <div className="rounded-full ring-1 ring-white/10 transition-transform hover:scale-[1.04]">
                <Avatar src={draft.avatarUrl || null} name={draft.displayName || '?'} size={76} />
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="subtle"
                  size="sm"
                  onClick={() => fileRef.current?.click()}
                >
                  Upload photo
                </Button>
                {draft.avatarUrl && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="border-danger/30 hover:bg-danger/[0.08] text-danger"
                    onClick={() => set('avatarUrl', '')}
                  >
                    Remove
                  </Button>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={onPickAvatar}
                />
              </div>
            </div>

            {/* Display name — changeable once every 14 days. */}
            <Field
              label="Display name"
              hint={
                nameLocked
                  ? `🔒 You can change this again in ${nameUnlockInDays} day(s).`
                  : undefined
              }
              error={nameBlank ? "Display name can't be blank." : undefined}
            >
              <Input
                maxLength={32}
                value={draft.displayName}
                onChange={(e) => set('displayName', e.target.value)}
                placeholder="Your name"
                disabled={nameLocked}
                className={cn(
                  TRANSLUCENT_FIELD,
                  nameBlank && 'border-danger/55 focus:border-danger',
                  nameLocked && 'cursor-not-allowed opacity-60',
                )}
              />
            </Field>

            {/* Username */}
            <Field label="Username" hint="Lowercase letters, numbers, underscore. 3–20 characters.">
              <div className="flex items-stretch overflow-hidden rounded-md border border-card-line bg-black/25 transition-colors focus-within:border-accent focus-within:bg-black/40">
                <span className="flex items-center pl-3.5 pr-1 text-sm text-lo">@</span>
                <input
                  value={draft.handle}
                  onChange={(e) => set('handle', slugifyHandle(e.target.value))}
                  placeholder="yourhandle"
                  maxLength={20}
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  className="flex-1 bg-transparent py-2.5 pl-0.5 text-sm text-hi outline-none placeholder:text-lo"
                />
                {status && (
                  <span
                    className={cn(
                      'flex items-center whitespace-nowrap px-3.5 text-[11px] font-bold',
                      status.cls,
                    )}
                  >
                    {status.text}
                  </span>
                )}
              </div>
            </Field>

            {/* Bio */}
            <Field label="Bio" hint={`${draft.bio.length}/160`}>
              <Textarea
                maxLength={160}
                rows={4}
                value={draft.bio}
                onChange={(e) => set('bio', e.target.value)}
                placeholder="One short line about you"
                className={cn('resize-none', TRANSLUCENT_FIELD)}
              />
            </Field>

            {/* Links stay with the form when the card is single-column. */}
            {!aside && linksField}
          </div>

          {/* Right column — About controls, then Links, then an optional footer
              (the Locker) which rides below the links and shifts down as more are
              added. Balances the columns instead of links piling up on the left. */}
          {aside && (
            <div className="min-w-0 space-y-6">
              {aside}
              {linksField}
              {asideFooter}
            </div>
          )}
        </div>

        {/* Pinned-theme landing previews — full-width row across the card bottom. */}
        <PinnedThemePreviews className="mt-8" />
      </SettingsCard>

      <SaveBar show={dirty} saving={saving} onDiscard={discard} onSave={save} />
    </>
  );
}
